/**
 * ResellerPanel Component (New Design System)
 *
 * Redesigned Reseller management panel using the new design system components.
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useOrgDetailLink } from '../../useOrgDetailLink';
import { Box, Grid, Typography, Chip, Stack } from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ArchiveOutlinedIcon from '@mui/icons-material/ArchiveOutlined';
import UnarchiveOutlinedIcon from '@mui/icons-material/UnarchiveOutlined';

// Design System Components
import {
    colors,
    EntityPageLayout,
    EntityPageHeader,
    InfoCard,
    ActivityCard,
    DraftTextField,
    DraftSelectField,
    ReadOnlyField,
    Button,
    ConfirmDialog,
} from '@components/design-system';

// Existing utilities and hooks
import { Operation } from '@/types/general';
import { useResellerMutation, useNameMapQuery, useResellerQuery } from '@features/redux/apis/org';
import { useAuditHistoryQuery } from '@features/redux/apis/audit';
import { Query } from '@features/redux/apis/utils';
import { Validate } from '@utils/validate';
import { getIdDisplayValue } from '@utils/index';
import { DEFAULT_BILLING_OPTIONS } from '@consts/company';
import { navigateToActivity } from '@utils/activityLinks';
import { ResellerEffects } from './ResellerEffects';
import { AppRoutes } from '@consts/routes';
import { BannerEditor } from '@components/banners';
import { BannerScope } from '@api/banners';

import { TrackingConfigCard } from '../TrackingConfigCard';
import { TrackingScopeType } from '@api/org';
import { OrgChainBreadcrumb } from '../../OrgChainBreadcrumb';
import { Selection } from '../../orgNavTypes';

import type { ManageProps } from '../Panel';
import type { RootState } from '@features/redux/store';

// Draft state setup
const useResellers = Query.withDraft(useResellerQuery, {
    ResellerCompanyID: undefined as unknown as number,
    CompanyName: '',
    Inactive: false,
    QBName: undefined,
    SalesRepTrackingEnabled: null,
    ...DEFAULT_BILLING_OPTIONS,
});

// Sales rep tracking options
const salesRepTrackingOptions = [
    { value: 'inherit', label: 'Inherit from Company' },
    { value: 'true', label: 'Enabled' },
    { value: 'false', label: 'Disabled' },
];

export const ResellerPanel: React.FC<ManageProps> = ({ operation, id: resellerId, onSuccess }) => {
    const navigate = useNavigate();
    const detailLink = useOrgDetailLink();
    const { role } = useSelector(({ auth }: RootState) => auth.Roles);
    const auditPk = resellerId ? `Reseller#${resellerId}` : null;

    // Fetch name maps for company lookup
    const { data: nameMap } = useNameMapQuery({});

    // Company options for linking
    const companyOptions = useMemo(
        () =>
            Object.entries(nameMap?.companies ?? {}).map(([key, value]) => ({
                value: Number(key),
                label: value as string,
            })),
        [nameMap?.companies]
    );

    // Fetch reseller data
    const {
        draft,
        data,
        isFetching: queryLoading,
    } = useResellers({ resellerId: +resellerId! }, { skip: operation === Operation.Create });
    const [draftState, setDraftState] = draft;

    // Mutation
    const [mutate, { isLoading: mutationLoading }] = useResellerMutation({
        fixedCacheKey: operation === Operation.Edit ? 'ResellerMutation' : 'ResellerCreation',
    });

    // Archive confirmation dialog state
    const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);

    // Sales rep tracking state
    const [salesRepTracking, setSalesRepTracking] = useState<string>('inherit');

    useEffect(() => {
        if (data?.SalesRepTrackingEnabled === null || data?.SalesRepTrackingEnabled === undefined) {
            setSalesRepTracking('inherit');
        } else {
            setSalesRepTracking(String(data?.SalesRepTrackingEnabled));
        }
    }, [data?.SalesRepTrackingEnabled]);

    const handleSalesRepChange = (value: string | number) => {
        const strValue = String(value);
        setSalesRepTracking(strValue);
        setDraftState((prev) => ({
            ...prev,
            SalesRepTrackingEnabled: strValue === 'inherit' ? null : strValue === 'true',
        }));
    };

    // Audit history
    const { data: auditData = [] } = useAuditHistoryQuery(
        { entityPk: auditPk! },
        { skip: !auditPk || operation === Operation.Create }
    );

    const handleSubmit = async () => {
        const result = await mutate({
            method: operation === Operation.Create ? 'POST' : 'PATCH',
            reseller: draftState,
        });

        // Navigate to edit page after successful creation
        if (operation === Operation.Create && 'data' in result && result.data?.CompanyID) {
            navigate(detailLink('reseller', result.data.CompanyID));
        } else {
            onSuccess?.();
        }
    };

    const handleArchiveToggle = async () => {
        await mutate({
            method: 'PATCH',
            reseller: { ...draftState, Inactive: !draftState.Inactive },
        });
        setDraftState((prev) => ({ ...prev, Inactive: !prev.Inactive }));
        setArchiveDialogOpen(false);
    };

    const isLoading = queryLoading || mutationLoading;
    const isValid = Validate.Org.reseller(draftState);
    const canSave = role.stores.edit;

    const pageTitle = operation === Operation.Create ? 'Create New Reseller' : data?.CompanyName || 'Reseller';

    // Get linked company name
    const linkedCompanyName = nameMap?.companies?.[draftState?.ResellerCompanyID] ?? '';

    return (
        <>
            <ResellerEffects />

            <EntityPageLayout loading={isLoading && !data}>
                <OrgChainBreadcrumb
                    ancestors={[
                        draftState?.ResellerCompanyID && linkedCompanyName
                            ? {
                                  type: Selection.Company,
                                  id: draftState.ResellerCompanyID,
                                  name: linkedCompanyName,
                              }
                            : null,
                    ]}
                    current={draftState?.CompanyName || 'Reseller'}
                />
                {/* Header */}
                <EntityPageHeader
                    entityType='reseller'
                    title={pageTitle}
                    id={operation !== Operation.Create ? resellerId : undefined}
                    icon={<StorefrontIcon />}
                    isCreate={operation === Operation.Create}
                    loading={isLoading && !data}
                    onCancel={onSuccess}
                    onSave={canSave ? handleSubmit : undefined}
                    saveDisabled={!isValid}
                    saving={mutationLoading}
                    statusBadge={
                        draftState.Inactive ? (
                            <Chip
                                size='small'
                                label='Archived'
                                sx={{
                                    backgroundColor: colors.warning.light,
                                    color: colors.warning.dark,
                                    fontWeight: 600,
                                }}
                            />
                        ) : undefined
                    }
                    actions={
                        operation !== Operation.Create && resellerId ? (
                            <Stack direction='row' spacing={1}>
                                <Button
                                    variant='secondary'
                                    onClick={() => setArchiveDialogOpen(true)}
                                    startIcon={
                                        draftState.Inactive ? <UnarchiveOutlinedIcon /> : <ArchiveOutlinedIcon />
                                    }>
                                    {draftState.Inactive ? 'Restore' : 'Archive'}
                                </Button>
                                <Button variant='secondary' onClick={() => navigateToActivity('reseller', resellerId)}>
                                    View Activity
                                </Button>
                            </Stack>
                        ) : undefined
                    }
                />

                {/* Reseller Info Card */}
                <InfoCard title='Reseller Information' icon={<InfoOutlinedIcon />}>
                    <Grid container spacing={2}>
                        {operation === Operation.Edit && role.isAdmin && (
                            <>
                                <ReadOnlyField label='Reseller ID' value={data?.CompanyID} size={6} mono />
                                <ReadOnlyField
                                    label='Linked Company ID'
                                    value={getIdDisplayValue(data?.ResellerCompanyID)}
                                    size={6}
                                    mono
                                />
                            </>
                        )}

                        {/* Linked Company Selection */}
                        {operation === Operation.Create ? (
                            <DraftSelectField
                                label='Linked Company'
                                stateKey='ResellerCompanyID'
                                draft={draft}
                                disabled={operation !== Operation.Create}
                                options={companyOptions}
                                size={12}
                                placeholder='Select a company to link...'
                                defaultFirst={false}
                            />
                        ) : (
                            <ReadOnlyField label='Linked Company' value={linkedCompanyName} size={12} />
                        )}

                        <DraftTextField
                            label='Reseller Name'
                            stateKey='CompanyName'
                            draft={draft}
                            disabled={operation === Operation.View}
                            validateFn={Validate.name}
                            helperText={{
                                valid: '',
                                invalid: 'Please provide a valid reseller name',
                            }}
                            size={12}
                            required
                        />

                        {/* Sales Rep Tracking */}
                        {role.stores.edit && (
                            <Grid item xs={12}>
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                                    <Typography
                                        variant='body2'
                                        sx={{
                                            fontWeight: 500,
                                            color: colors.neutral[700],
                                            fontSize: '0.8125rem',
                                        }}>
                                        Sales Rep Tracking
                                    </Typography>
                                    <DraftSelectField
                                        label=''
                                        stateKey='SalesRepTrackingEnabled'
                                        draft={
                                            [
                                                { SalesRepTrackingEnabled: salesRepTracking },
                                                (fn: any) => {
                                                    const newState =
                                                        typeof fn === 'function'
                                                            ? fn({ SalesRepTrackingEnabled: salesRepTracking })
                                                            : fn;
                                                    handleSalesRepChange(newState.SalesRepTrackingEnabled);
                                                },
                                            ] as any
                                        }
                                        disabled={operation === Operation.View}
                                        options={salesRepTrackingOptions}
                                        size={12}
                                    />
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                </InfoCard>

                {/* Super admin only until the consumables service goes live */}
                {operation !== Operation.Create && resellerId && role.isSuperAdmin && (
                    <TrackingConfigCard
                        scopeType={TrackingScopeType.Reseller}
                        scopeId={+resellerId}
                        canEdit={canSave}
                        inheritLabel='Inherit from company'
                    />
                )}

                {operation !== Operation.Create && resellerId && (
                    <BannerEditor
                        scope={BannerScope.Reseller}
                        companyId={+resellerId}
                        canEdit={canSave}
                        title='Reseller banners'
                        description='These banners override the company/global defaults for kiosks under this reseller. Leave empty to inherit the company set, falling back to global.'
                    />
                )}

                {/* Audit Log */}
                {auditPk && operation !== Operation.Create && role.isAdmin && auditData.length > 0 && (
                    <ActivityCard audits={auditData} entityPk={auditPk} maxItems={5} />
                )}
            </EntityPageLayout>

            {/* Archive/Restore Confirmation Dialog */}
            <ConfirmDialog
                open={archiveDialogOpen}
                onClose={() => setArchiveDialogOpen(false)}
                onConfirm={handleArchiveToggle}
                title={draftState.Inactive ? 'Restore Reseller' : 'Archive Reseller'}
                message={
                    draftState.Inactive
                        ? `Are you sure you want to restore "${draftState.CompanyName}"? This reseller will become visible in the active resellers list.`
                        : `Are you sure you want to archive "${draftState.CompanyName}"? Archived resellers are hidden from the main list but can be restored later.`
                }
                confirmText={draftState.Inactive ? 'Restore' : 'Archive'}
                variant={draftState.Inactive ? 'success' : 'warning'}
                loading={mutationLoading}
            />
        </>
    );
};

export default ResellerPanel;
